"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Copy, Check, Trash2, GitCompare, Plus, Minus, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

interface DiffResult {
  path: string
  type: "added" | "removed" | "changed" | "unchanged"
  oldValue?: unknown
  newValue?: unknown
}

function getType(value: unknown): string {
  if (value === null) return "null"
  if (Array.isArray(value)) return "array"
  return typeof value
}

function deepDiff(
  obj1: unknown,
  obj2: unknown,
  path: string = "",
  results: DiffResult[] = []
): DiffResult[] {
  const type1 = getType(obj1)
  const type2 = getType(obj2)

  // Different types
  if (type1 !== type2) {
    results.push({ path: path || "(root)", type: "changed", oldValue: obj1, newValue: obj2 })
    return results
  }

  // Primitives
  if (type1 !== "object" && type1 !== "array") {
    if (obj1 !== obj2) {
      results.push({ path: path || "(root)", type: "changed", oldValue: obj1, newValue: obj2 })
    }
    return results
  }

  // Arrays
  if (type1 === "array") {
    const arr1 = obj1 as unknown[]
    const arr2 = obj2 as unknown[]
    const maxLen = Math.max(arr1.length, arr2.length)

    for (let i = 0; i < maxLen; i++) {
      const newPath = path ? `${path}[${i}]` : `[${i}]`
      if (i >= arr1.length) {
        results.push({ path: newPath, type: "added", newValue: arr2[i] })
      } else if (i >= arr2.length) {
        results.push({ path: newPath, type: "removed", oldValue: arr1[i] })
      } else {
        deepDiff(arr1[i], arr2[i], newPath, results)
      }
    }
    return results
  }

  // Objects
  const o1 = obj1 as Record<string, unknown>
  const o2 = obj2 as Record<string, unknown>
  const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)])

  for (const key of allKeys) {
    const newPath = path ? `${path}.${key}` : key
    if (!(key in o1)) {
      results.push({ path: newPath, type: "added", newValue: o2[key] })
    } else if (!(key in o2)) {
      results.push({ path: newPath, type: "removed", oldValue: o1[key] })
    } else {
      deepDiff(o1[key], o2[key], newPath, results)
    }
  }

  return results
}

function formatValue(value: unknown): string {
  if (value === undefined) return "undefined"
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const sampleJson1 = `{
  "name": "John Doe",
  "age": 30,
  "email": "john@example.com",
  "address": {
    "city": "New York",
    "zip": "10001"
  },
  "hobbies": ["reading", "gaming"]
}`

const sampleJson2 = `{
  "name": "John Smith",
  "age": 31,
  "phone": "+1234567890",
  "address": {
    "city": "New York",
    "zip": "10002",
    "country": "USA"
  },
  "hobbies": ["reading", "coding", "gaming"]
}`

export function JsonDiff() {
  const [json1, setJson1] = useState("")
  const [json2, setJson2] = useState("")
  const [diff, setDiff] = useState<DiffResult[]>([])
  const [error, setError] = useState<string | null>(null)
  const [_showUnchanged, _setShowUnchanged] = useState(false)
  const [copied, setCopied] = useState(false)

  const compare = useCallback(() => {
    setError(null)
    setDiff([])

    if (!json1.trim() || !json2.trim()) {
      setError("Please provide both JSON inputs")
      return
    }

    try {
      const obj1 = JSON.parse(json1)
      const obj2 = JSON.parse(json2)
      const results = deepDiff(obj1, obj2)
      setDiff(results)

      if (results.length === 0) {
        toast.success("No differences found")
      } else {
        toast.success(`Found ${results.length} difference(s)`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Invalid JSON"
      setError(message)
      toast.error("Invalid JSON")
    }
  }, [json1, json2])

  const loadSample = () => {
    setJson1(sampleJson1)
    setJson2(sampleJson2)
    setDiff([])
    setError(null)
  }

  const clear = () => {
    setJson1("")
    setJson2("")
    setDiff([])
    setError(null)
  }

  const swap = () => {
    setJson1(json2)
    setJson2(json1)
    setDiff([])
  }

  const copyDiff = async () => {
    const text = diff
      .map((d) => {
        const prefix = d.type === "added" ? "+" : d.type === "removed" ? "-" : "~"
        if (d.type === "changed") {
          return `${prefix} ${d.path}: ${formatValue(d.oldValue)} → ${formatValue(d.newValue)}`
        }
        return `${prefix} ${d.path}: ${formatValue(d.oldValue ?? d.newValue)}`
      })
      .join("\n")

    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Diff copied to clipboard")
  }

  const addedCount = diff.filter((d) => d.type === "added").length
  const removedCount = diff.filter((d) => d.type === "removed").length
  const changedCount = diff.filter((d) => d.type === "changed").length

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            JSON Diff
          </CardTitle>
          <CardDescription>
            Compare two JSON objects and see the differences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={loadSample}>
              Load Sample
            </Button>
            <Button variant="outline" size="sm" onClick={swap}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Swap
            </Button>
            <Button variant="outline" size="sm" onClick={clear}>
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>

          {/* JSON Inputs */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="json1">Original JSON</Label>
              <Textarea
                id="json1"
                placeholder="Paste original JSON here..."
                value={json1}
                onChange={(e) => setJson1(e.target.value)}
                className="font-mono text-sm min-h-[250px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="json2">Modified JSON</Label>
              <Textarea
                id="json2"
                placeholder="Paste modified JSON here..."
                value={json2}
                onChange={(e) => setJson2(e.target.value)}
                className="font-mono text-sm min-h-[250px]"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Compare Button */}
          <Button onClick={compare} disabled={!json1.trim() || !json2.trim()}>
            <GitCompare className="h-4 w-4 mr-2" />
            Compare
          </Button>

          {/* Results */}
          {diff.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label>Differences</Label>
                  <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                    <Plus className="h-3 w-3 mr-1" />
                    {addedCount} added
                  </Badge>
                  <Badge variant="secondary" className="bg-red-500/10 text-red-600">
                    <Minus className="h-3 w-3 mr-1" />
                    {removedCount} removed
                  </Badge>
                  <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600">
                    <RefreshCw className="h-3 w-3 mr-1" />
                    {changedCount} changed
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={copyDiff}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              </div>

              <div className="border rounded-lg divide-y max-h-[400px] overflow-auto">
                {diff.map((d, i) => (
                  <div
                    key={i}
                    className={cn(
                      "p-3 text-sm font-mono",
                      d.type === "added" && "bg-green-500/5",
                      d.type === "removed" && "bg-red-500/5",
                      d.type === "changed" && "bg-yellow-500/5"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={cn(
                          "flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-xs font-bold",
                          d.type === "added" && "bg-green-500 text-white",
                          d.type === "removed" && "bg-red-500 text-white",
                          d.type === "changed" && "bg-yellow-500 text-white"
                        )}
                      >
                        {d.type === "added" ? "+" : d.type === "removed" ? "-" : "~"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-primary font-medium">{d.path}</span>
                        {d.type === "changed" ? (
                          <div className="mt-1 space-y-1">
                            <div className="text-red-600 line-through">
                              {formatValue(d.oldValue)}
                            </div>
                            <div className="text-green-600">
                              {formatValue(d.newValue)}
                            </div>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              "mt-1",
                              d.type === "added" && "text-green-600",
                              d.type === "removed" && "text-red-600"
                            )}
                          >
                            {formatValue(d.type === "added" ? d.newValue : d.oldValue)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No differences */}
          {diff.length === 0 && json1 && json2 && !error && (
            <div className="text-center py-8 text-muted-foreground">
              <GitCompare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Click &quot;Compare&quot; to see differences</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
